import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import type { ApiError } from '@/types';

/**
 * API Base URL for echoes.mobi
 *
 * In production: Use our CloudFront proxy (/api) to avoid CORS issues
 * In development: Call echoes.mobi directly (localhost is typically allowed)
 */
export const API_BASE_URL = import.meta.env.PROD
  ? '/api' // Production: use CloudFront -> API Gateway -> echoes.mobi
  : 'https://echoes.mobi'; // Development: direct to echoes.mobi

/**
 * Default timeout for API requests (30 seconds)
 */
const DEFAULT_TIMEOUT = 30000;

/**
 * Maximum number of retry attempts for failed requests
 */
const MAX_RETRIES = 3;

/**
 * Delay between retry attempts (in ms)
 */
const RETRY_DELAY = 1000;

/**
 * HTTP status codes that should trigger a retry
 */
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

/**
 * Create and configure the Axios instance
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: DEFAULT_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  // Add request interceptor for logging (development only)
  client.interceptors.request.use(
    (config) => {
      if (import.meta.env.DEV) {
        console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor for error handling
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      return handleApiError(error, client);
    }
  );

  return client;
}

/**
 * Handle API errors with retry logic
 */
async function handleApiError(error: AxiosError, client: AxiosInstance): Promise<never> {
  const config = error.config as AxiosRequestConfig & { retryCount?: number };

  // Initialize retry count
  if (!config.retryCount) {
    config.retryCount = 0;
  }

  // Determine if we should retry
  const shouldRetry =
    config.retryCount < MAX_RETRIES &&
    error.response &&
    RETRYABLE_STATUS_CODES.includes(error.response.status);

  if (shouldRetry) {
    config.retryCount++;

    // Calculate backoff delay (exponential backoff)
    const delay = RETRY_DELAY * Math.pow(2, config.retryCount - 1);

    if (import.meta.env.DEV) {
      console.log(
        `[API] Retry attempt ${config.retryCount}/${MAX_RETRIES} after ${delay}ms`,
        error.config?.url
      );
    }

    // Wait before retrying
    await new Promise((resolve) => setTimeout(resolve, delay));

    // Retry the request
    return client.request(config);
  }

  // Convert Axios error to our ApiError format
  throw createApiError(error);
}

/**
 * Convert Axios error to ApiError
 */
function createApiError(error: AxiosError): ApiError {
  if (error.response) {
    // Server responded with error status
    return {
      message:
        (error.response.data as { message?: string })?.message ||
        error.message ||
        'An error occurred',
      status: error.response.status,
      code: error.code,
    };
  } else if (error.request) {
    // Request made but no response received
    return {
      message: 'No response received from server',
      code: error.code || 'NETWORK_ERROR',
    };
  } else {
    // Error setting up the request
    return {
      message: error.message || 'Failed to make request',
      code: error.code || 'REQUEST_ERROR',
    };
  }
}

/**
 * Singleton API client instance
 */
export const apiClient = createApiClient();

/**
 * Helper function to make GET requests with error handling
 */
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.get<T>(url, config);
  return response.data;
}

/**
 * Helper function to make POST requests with error handling
 */
export async function post<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
}

/**
 * Helper function to check API health
 */
export async function checkApiHealth(): Promise<boolean> {
  try {
    await apiClient.get('/item_categories?page=1');
    return true;
  } catch {
    return false;
  }
}
